import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, ShoppingBag, Sparkles, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import InstallButton from "@/components/InstallButton";

export const scrollToSection = (id) => {
  const el = document.querySelector(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -76 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const links = [
  { label: "The Collection", target: "#shop" },
  { label: "Age Guide", target: "#size-guide" },
  { label: "Craft", target: "#manifesto" },
  { label: "Reviews", target: "#reviews" },
];

export default function Navbar() {
  const { cartCount, wishlist, setCartOpen, setTrackOpen, customer, setLoginOpen, setOrdersOpen } = useStore();
  const [showBar, setShowBar] = useState(() => localStorage.getItem("lj_announce_dismissed") !== "1");
  const dismissBar = () => {
    localStorage.setItem("lj_announce_dismissed", "1");
    setShowBar(false);
  };
  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-cream/90 border-b border-ink/10"
      data-testid="navbar"
    >
      <AnimatePresence>
        {showBar && (
          <motion.div
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="bg-terracotta text-white overflow-hidden"
            data-testid="announcement-bar"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2.5 flex items-center justify-center gap-3 text-xs sm:text-sm">
              <Sparkles className="w-3.5 h-3.5 shrink-0" />
              <p className="font-medium text-center">
                The Festive Edit is live — kurtas & dresses for the season. Free shipping above ₹1,999.
              </p>
              <button
                onClick={() => scrollToSection("#shop")}
                className="underline underline-offset-2 font-semibold whitespace-nowrap hover:text-peach transition-colors"
                data-testid="announcement-cta"
              >
                Shop now
              </button>
              <button
                onClick={dismissBar}
                className="ml-1 rounded-full p-1 hover:bg-white/20 transition-colors shrink-0"
                data-testid="announcement-close"
                aria-label="Dismiss announcement"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
        <button onClick={() => scrollToSection("#top")} className="flex items-center gap-2.5" data-testid="navbar-logo">
          <img src="/logo.png" alt="Baeboo's Wardrobe logo" className="h-11 w-11 object-contain" data-testid="navbar-logo-img" />
          <span className="font-serif font-black text-2xl tracking-tight text-ink">Baeboo's</span>
          <span className="font-mono text-[10px] tracking-[0.3em] text-sage-dark uppercase">Wardrobe</span>
        </button>
        <nav className="hidden md:flex items-center gap-8" data-testid="navbar-links">
          {links.map((l) => (
            <button
              key={l.target}
              onClick={() => scrollToSection(l.target)}
              className="text-sm font-medium text-charcoal hover:text-terracotta transition-colors duration-300"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <span className="relative hidden sm:flex items-center gap-1 text-charcoal px-3 py-2" data-testid="nav-wishlist-count">
            <Heart className="w-5 h-5" />
            {wishlist.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-terracotta text-white text-[10px] font-mono w-4 h-4 rounded-full flex items-center justify-center">
                {wishlist.length}
              </span>
            )}
          </span>
          <button
            onClick={() => setTrackOpen(true)}
            className="hidden sm:block font-mono text-[11px] tracking-widest uppercase text-charcoal hover:text-terracotta transition-colors px-3 py-2"
            data-testid="nav-track-order-btn"
          >
            Track Order
          </button>
          {customer ? (
            <button
              onClick={() => setOrdersOpen(true)}
              className="hidden sm:block font-mono text-[11px] tracking-widest uppercase text-terracotta font-bold hover:text-terracotta-dark transition-colors px-3 py-2"
              data-testid="nav-my-orders-btn"
            >
              My Orders
            </button>
          ) : (
            <button
              onClick={() => setLoginOpen(true)}
              className="hidden sm:block font-mono text-[11px] tracking-widest uppercase text-charcoal hover:text-terracotta transition-colors px-3 py-2"
              data-testid="nav-login-btn"
            >
              Login
            </button>
          )}
          <InstallButton />
          <button
            onClick={() => setCartOpen(true)}
            className="relative flex items-center gap-2 bg-ink text-cream rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-terracotta transition-colors duration-300"
            data-testid="nav-cart-trigger"
          >
            <ShoppingBag className="w-4 h-4" />
            Basket
            {cartCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-terracotta text-white text-[10px] font-mono w-5 h-5 rounded-full flex items-center justify-center" data-testid="nav-cart-count">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </motion.header>
  );
}
